/**
 * E46 claim index (2026-07-18).
 *
 * Purpose: let a Buy click know whether the shopper had already negotiated
 * for that exact retailer + product, so we can answer the question the funnel
 * could not previously answer , "the retailer said yes, did they actually
 * buy?" , and compute the ghost rate (E49).
 *
 * Why a registry instead of props/hooks:
 * the Buy button is rendered from four different places (Hero, two
 * ComparisonRow variants, AlsoFoundSection), none of which currently consume
 * the claims context. Threading a hook through all four would mean touching
 * four render paths, and any Buy button added later would silently miss the
 * linkage. Instead `SubmittedClaimsProvider` , which already loads the
 * shopper's claims , publishes a lookup here once, and the analytics helper
 * resolves it at click time. Every Buy click is linked, including future ones.
 *
 * Layering: this is a lib module, imported by both the provider (a component)
 * and the analytics helper (a lib). Nothing in lib imports components.
 *
 * The lookup is a closure supplied by the provider, so this module never needs
 * to know how claims are matched to products (that logic stays in
 * claimMatchKey).
 */

/** Minimal shape needed to identify a product row. */
export interface ClaimIndexProduct {
  retailer_slug: string;
  retailer_sku?: string | null;
  product_url?: string | null;
}

/** What a Buy click records about the claim it belongs to. */
export interface ClaimRef {
  transactionId: string;
  /** Claim status at click time: sent / approved / refused / ... */
  status: string;
}

type LookupFn = (product: ClaimIndexProduct) => ClaimRef | null;

let _lookup: LookupFn | null = null;

/**
 * Publish the lookup. Called by SubmittedClaimsProvider whenever the
 * shopper's claims load or change. Pass null on unmount / sign-out so a stale
 * index can never attribute a later click to an old claim.
 */
export function setClaimLookup(fn: LookupFn | null): void {
  _lookup = fn;
}

/**
 * Resolve the claim for a product, or null when there is none (the common
 * case: most Buy clicks are not preceded by a negotiation). Never throws , a
 * broken index must not break a Buy click.
 */
export function lookupClaim(
  product: ClaimIndexProduct | null | undefined,
): ClaimRef | null {
  if (!_lookup || !product) return null;
  try {
    return _lookup(product);
  } catch {
    return null;
  }
}
